// This ism eant for use by the modal.  Can only be applied to one element on the page.
// Helps hold the underlying page in position, while allowing the modal to scroll smoothly.
// Most of this code is due to the odd behavior of iOS.
//
// This is a trimmed down version of https://github.com/willmcpo/body-scroll-lock
// Notes from that library:
// Adopted and modified solution from Bohdan Didukh (2017)
// eslint-disable-next-line max-len
// https://stackoverflow.com/questions/41594997/ios-10-safari-prevent-scrolling-behind-a-fixed-overlay-and-maintain-scroll-posi

import { isIOS, isAndroid } from 'util/browser-utils'

let targetElement: HTMLElement
let scrollY: number
let initialClientY = -1
let previousBodyOverflowSetting

function allowMove(e) {
  return e.target && e.target.className.indexOf('tt-scoll-lock-ignore') > -1
}

const preventDefault = (rawEvent): boolean => {
  const e = rawEvent || window.event

  if (allowMove(e)) {
    return true
  }

  // Do not prevent if the event has more than one touch (usually meaning this is a multi touch
  // gesture like pinch to zoom)
  if (e.touches.length > 1) return true

  if (e.preventDefault) e.preventDefault()

  return false
}

// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight#Problems_and_solutions
const isTargetElementTotallyScrolled = (targetElement): boolean =>
  targetElement ?
    targetElement.scrollHeight - targetElement.scrollTop <= targetElement.clientHeight
    : false

const handleScroll = (e, targetElement): boolean => {
  const clientY = e.targetTouches[0].clientY - initialClientY

  if (allowMove(e)) {
    return true
  }

  if (targetElement && targetElement.scrollTop === 0 && clientY > 0) {
    // element is at the top of its scroll
    return preventDefault(e)
  }

  if (isTargetElementTotallyScrolled(targetElement) && clientY < 0) {
    // element is at the top of its scroll
    return preventDefault(e)
  }

  e.stopPropagation()
  return true
}

export const disableBodyScroll = (el: HTMLElement): void => {
  // already disabled by something else
  if (targetElement) {
    return
  }
  targetElement = el
  if (isIOS) {
    targetElement.style['-webkit-overflow-scrolling'] = 'touch'
    targetElement.ontouchstart = (e) => {
      if (e.targetTouches.length === 1) {
        // detect single touch
        initialClientY = e.targetTouches[0].clientY
      }
    }
    targetElement.ontouchmove = (e) => {
      if (e.targetTouches.length === 1) {
        // detect single touch
        handleScroll(e, targetElement)
      }
    }

    document.addEventListener('touchmove', preventDefault)
  } else {
    // Setting overflow on body/documentElement synchronously in Desktop Safari slows down
    // the responsiveness for some reason. Setting within a setTimeout fixes this.
    setTimeout(() => {
      // TT-11611 - form scrolling fix
      if (isAndroid) {
        scrollY = window.scrollY
        window.scrollTo(window.scrollX, 0)
      }
      // If previousBodyOverflowSetting is already set, don't set it again.
      if (previousBodyOverflowSetting === undefined) {
        previousBodyOverflowSetting = document.body.style.overflow
        document.body.style.overflow = 'hidden'
      }
    })
  }
}

export const enableBodyScroll = (): void => {
  if (isIOS) {
    if (targetElement) {
      targetElement.ontouchstart = null
      targetElement.ontouchmove = null
    }

    document.removeEventListener('touchmove', preventDefault)
  } else {
    // Setting overflow on body/documentElement synchronously in Desktop Safari slows down
    // the responsiveness for some reason. Setting within a setTimeout fixes this.
    setTimeout(() => {
      if (isAndroid && scrollY) {
        window.scrollTo(window.scrollX, scrollY)
      }
      if (previousBodyOverflowSetting !== undefined) {
        document.body.style.overflow = previousBodyOverflowSetting

        // Restore previousBodyOverflowSetting to undefined
        // so setOverflowHidden knows it can be set again.
        previousBodyOverflowSetting = undefined
      }
    })
  }
  targetElement = undefined
}
